import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { mdToHtml } from '@/lib/markdown';
import { toast } from '@/components/ui/sonner';
import {
  Sparkle, PaperPlaneRight, CalendarCheck, Clock, MapPin, CurrencyDollar,
  ClipboardText, ListChecks, Users, ChatCircle, Confetti, ArrowRight, CheckCircle,
  ArrowClockwise,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING = "Hi! I'm your event coordinator 🎯 Your vendors are booked — now I'll help you pull the day together: confirmations, a run-of-show, logistics, payments, all of it. What should we tackle first?";

const SUGGESTIONS = [
  'Build my run-of-show',
  'What should I confirm with each vendor?',
  'When are my payments due?',
  'Draft a message to a vendor',
];

// Mirrors what a real planner does after booking. Each has a stable `key` so the
// customer's checked-off state can persist across sessions.
const CHECKLIST = [
  { key: 'confirm', icon: <CheckCircle size={16} weight="fill" />, title: 'Confirm your vendors', desc: 'Get arrival times, setup windows & what’s included in writing.',
    prompt: 'Help me confirm the details (arrival time, setup/teardown window, what’s included) with each of my booked vendors.' },
  { key: 'runofshow', icon: <ClipboardText size={16} weight="fill" />, title: 'Build your run-of-show', desc: 'A minute-by-minute event-day timeline.',
    prompt: 'Build me a run-of-show timeline for my event day based on my booked vendors.' },
  { key: 'logistics', icon: <ListChecks size={16} weight="fill" />, title: 'Lock the logistics', desc: 'Catering headcount, AV, decor, staffing, parking, guest flow.',
    prompt: 'Walk me through the logistics I still need to plan — catering headcount, AV/sound, decor, parking, and guest flow.' },
  { key: 'payments', icon: <CurrencyDollar size={16} weight="fill" />, title: 'Stay on top of payments', desc: 'Deadlines and balances due before the day.',
    prompt: 'When are my payments and balances due, and what should I confirm about them?' },
  { key: 'prep', icon: <Users size={16} weight="fill" />, title: 'Prep for the day', desc: 'Contact sheet + a plan for last-minute problems.',
    prompt: 'Help me prep for the day itself — a day-of contact sheet and a plan for handling last-minute problems.' },
  { key: 'wrapup', icon: <Confetti size={16} weight="fill" />, title: 'Wrap-up plan', desc: 'Final payments, thank-yous & reviews.',
    prompt: 'What should I do after the event — final payments, thank-yous, and leaving reviews for my vendors?' },
];

const today = () => new Date().toISOString().slice(0, 10);
const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const target = new Date(dateStr + 'T00:00');
  if (Number.isNaN(target.getTime())) return null;   // non-ISO / bad date → no countdown, never "NaN"
  const ms = target - new Date(today() + 'T00:00');
  return Math.round(ms / 86400000);
};
// Short, friendly label for an event tab, e.g. "Sat, Sep 19".
const fmtEventDate = (iso) => {
  try {
    const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return iso; }
};

// Pull VENDOR_MSG[token|name|message] markers out of an assistant reply. Returns
// { text: the reply with markers stripped, sends: [{token, name, body}] } so the
// prose renders cleanly and each drafted vendor message becomes a send card.
// The body is captured greedily to the FINAL ']' on the line (via the 'm' flag +
// $-anchor), so a message containing '[' or ']' (e.g. "arrive by 4pm [main gate]")
// is NOT truncated at the first bracket. token/name still can't contain '|' or ']'.
const VENDOR_MSG_RE = /VENDOR_MSG\[([^\]|]+)\|([^|]*)\|(.*)\]\s*$/gm;
const parseVendorMsgs = (content) => {
  const sends = [];
  const text = (content || '').replace(VENDOR_MSG_RE, (_, token, name, body) => {
    const t = (token || '').trim(), n = (name || '').trim(), b = (body || '').trim();
    if (t && b) sends.push({ token: t, name: n || 'your vendor', body: b });
    return '';   // drop the raw marker from the visible text
  }).replace(/\n{3,}/g, '\n\n').trim();
  return { text, sends };
};

export default function CoordinatorPage() {
  const { user, token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([{ role: 'assistant', content: GREETING }]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [briefing, setBriefing] = useState(false);   // fetching the proactive opening
  const [sentSends, setSentSends] = useState({});    // {"token::body-hash": 'sending'|'sent'|'error'} for one-tap vendor sends
  const scrollRef = useRef(null);

  const [doneKeys, setDoneKeys] = useState([]);
  const [eventDate, setEventDate] = useState('');   // '' = all events / no scoping

  // Load bookings + checklist once, and pick the soonest event as the default.
  useEffect(() => {
    const h = { headers: { Authorization: `Bearer ${token}` } };
    Promise.all([
      axios.get(`${API}/bookings`, h).catch(() => ({ data: [] })),
      axios.get(`${API}/coordinator/checklist`, h).catch(() => ({ data: { done: [] } })),
    ]).then(([bk, chk]) => {
      const bs = Array.isArray(bk.data) ? bk.data : [];
      setBookings(bs);
      setDoneKeys(Array.isArray(chk.data?.done) ? chk.data.done : []);
      // Default the scope to the soonest upcoming event date, if any.
      const upcomingDates = [...new Set(bs
        .filter((b) => (b.booking_date || '') >= today())
        .map((b) => b.booking_date).filter(Boolean))].sort();
      if (upcomingDates.length) setEventDate(upcomingDates[0]);
    }).finally(() => setLoading(false));
  }, [token]);

  // Load the saved conversation + checklist for the SELECTED event when it changes.
  useEffect(() => {
    if (loading) return;
    // Guard against a fetch race: if the user switches events while these are in
    // flight, a late (older) response must not overwrite the now-selected event's
    // chat/checklist. The cleanup flips `cancelled` on the superseded run.
    let cancelled = false;
    // Switching events loads a different thread with its own drafts — clear the
    // one-tap send state so a same-vendor re-draft doesn't inherit a stale "Sent".
    setSentSends({});
    const h = { headers: { Authorization: `Bearer ${token}` } };
    const q = `event_date=${encodeURIComponent(eventDate)}`;
    axios.get(`${API}/coordinator/history?${q}`, h)
      .then((r) => {
        if (cancelled) return;
        const saved = Array.isArray(r.data?.messages) ? r.data.messages : [];
        if (saved.length) {
          // Returning to an ongoing thread — keep the greeting + saved turns.
          setMessages([{ role: 'assistant', content: GREETING }, ...saved]);
        } else {
          // Fresh thread → let the coordinator LEAD with a proactive briefing that
          // already knows the countdown, balance due, and unconfirmed vendors.
          setBriefing(true);
          axios.get(`${API}/coordinator/briefing?${q}`, h)
            .then((br) => { if (!cancelled) setMessages([{ role: 'assistant', content: br.data?.reply || GREETING }]); })
            .catch(() => { if (!cancelled) setMessages([{ role: 'assistant', content: GREETING }]); })
            .finally(() => { if (!cancelled) setBriefing(false); });
        }
      })
      .catch(() => { if (!cancelled) setMessages([{ role: 'assistant', content: GREETING }]); });
    axios.get(`${API}/coordinator/checklist?${q}`, h)
      .then((r) => { if (!cancelled) setDoneKeys(Array.isArray(r.data?.done) ? r.data.done : []); })
      .catch(() => { if (!cancelled) setDoneKeys([]); });
    return () => { cancelled = true; };
  }, [eventDate, loading, token]);

  // One-tap: send a coordinator-drafted message to a booked vendor. Keyed by
  // token + a cheap hash of the body so identical drafts to the same vendor
  // don't collide and each button tracks its own sending/sent state.
  const sendKey = (s) => `${s.token}::${s.body.length}:${s.body.slice(0, 24)}`;
  const sendVendorMsg = async (s) => {
    const key = sendKey(s);
    if (sentSends[key] === 'sending' || sentSends[key] === 'sent') return;
    if (!window.confirm(`Send this message to ${s.name}?\n\n"${s.body}"`)) return;
    setSentSends((p) => ({ ...p, [key]: 'sending' }));
    try {
      const resp = await axios.post(`${API}/coordinator/send-message`, { provider_id: s.token, body: s.body }, { headers: { Authorization: `Bearer ${token}` } });
      // The server blocks messages that contain contact info (phone/email/"call me")
      // and delivers a placeholder instead — so a 200 here does NOT mean the text
      // the user saw actually went out. Surface that instead of a false "Sent".
      if (resp?.data?.blocked) {
        setSentSends((p) => ({ ...p, [key]: 'blocked' }));
        toast.error('That message looked like it shared contact info, so it was held back. Rephrase it to keep everything on Bookr and try again.');
      } else {
        setSentSends((p) => ({ ...p, [key]: 'sent' }));
      }
    } catch (e) {
      setSentSends((p) => ({ ...p, [key]: 'error' }));
      toast.error(e?.response?.data?.detail || 'Could not send that message. Please try again.');
    }
  };

  const toggleTask = (key) => {
    setDoneKeys((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      // Persist (fire-and-forget) so it survives refresh / device change.
      axios.put(`${API}/coordinator/checklist`, { done: next, event_date: eventDate || undefined }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      return next;
    });
  };

  const clearChat = async () => {
    const h = { headers: { Authorization: `Bearer ${token}` } };
    const q = `event_date=${encodeURIComponent(eventDate)}`;
    try { await axios.delete(`${API}/coordinator/history?${q}`, h); } catch { /* ignore */ }
    // A fresh thread re-drafts messages; clear send state so an identical re-draft
    // isn't stuck showing "Sent" from the previous conversation.
    setSentSends({});
    // Re-lead with a fresh proactive briefing rather than the static greeting.
    setBriefing(true);
    try {
      const br = await axios.get(`${API}/coordinator/briefing?${q}`, h);
      setMessages([{ role: 'assistant', content: br.data?.reply || GREETING }]);
    } catch {
      setMessages([{ role: 'assistant', content: GREETING }]);
    } finally { setBriefing(false); }
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Distinct upcoming event dates → the events the customer can coordinate.
  const eventDates = [...new Set(bookings
    .filter(b => (b.booking_date || '') >= today())
    .map(b => b.booking_date).filter(Boolean))].sort();

  // The bookings/countdown/checklist all scope to the SELECTED event's date (when
  // one is picked); with no selection they span everything upcoming.
  const scoped = bookings.filter(b =>
    eventDate ? b.booking_date === eventDate : (b.booking_date || '') >= today());
  const upcoming = scoped.sort((a, b) => (a.booking_date || '').localeCompare(b.booking_date || ''));
  const countdown = upcoming.length ? daysUntil(upcoming[0].booking_date) : null;
  // Any scoped booking still has a balance owed → flag the payments task.
  const balanceDue = upcoming.some(b => (b.balance_amount || 0) > 0 && !b.balance_paid);
  const unconfirmed = upcoming.filter(b => b.status === 'pending').length;

  // Dynamic starter chips driven by the real event state — a real planner offers
  // the timely next step, not a fixed menu. Falls back to the static list.
  const suggestions = (() => {
    if (!upcoming.length) return SUGGESTIONS;
    const out = [];
    if (unconfirmed > 0) out.push(`Confirm my ${unconfirmed === 1 ? 'unconfirmed vendor' : `${unconfirmed} unconfirmed vendors`}`);
    if (balanceDue) out.push('When are my payments due?');
    if (countdown !== null && countdown <= 7) out.push('Build my day-of run-of-show');
    out.push('Draft a message to a vendor');
    // Pad from the static list so there are always a few options.
    for (const s of SUGGESTIONS) { if (out.length >= 4) break; if (!out.includes(s)) out.push(s); }
    return out.slice(0, 4);
  })();

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    const convo = [...messages, { role: 'user', content }];
    setMessages(convo);
    setInput('');
    setSending(true);
    try {
      const firstUser = convo.findIndex(m => m.role === 'user');
      const apiMessages = firstUser >= 0 ? convo.slice(firstUser) : convo;
      const r = await axios.post(`${API}/coordinator/chat`, { messages: apiMessages, event_date: eventDate || undefined }, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(m => [...m, { role: 'assistant', content: r.data.reply || "Sorry, I didn't catch that — could you try again?" }]);
    } catch (err) {
      setMessages(m => [...m, { role: 'assistant', content: "Sorry, I'm having trouble connecting right now. Please try again in a moment." }]);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };
  const started = messages.some(m => m.role === 'user');

  const payLabel = (b) => {
    if ((b.balance_amount || 0) > 0 && !b.balance_paid) return `Balance $${Math.round(b.balance_amount)} due ${b.balance_due || 'soon'}`;
    if (b.status === 'confirmed' || b.status === 'completed' || (b.total_paid || 0) > 0) return `Paid $${Math.round(b.total_paid || 0)}`;
    return 'Payment pending';
  };

  return (
    <div className="min-h-screen bg-background" data-testid="coordinator-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><Sparkle size={18} weight="fill" /></span>
            <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight text-foreground truncate">Event Coordinator</h1>
          </div>
          {started && (
            <button type="button" onClick={clearChat}
              className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground border border-border bg-card rounded-lg px-3 h-9 btn-anim hover:bg-secondary/60 hover:text-foreground cursor-pointer"
              data-testid="coordinator-new-chat">
              <ArrowClockwise size={14} /> New chat
            </button>
          )}
        </div>
        <p className="text-muted-foreground text-sm mb-6">Your AI planner for the run-up to the big day — ask it anything. Your conversation is saved.</p>

        {/* Event picker — only when the customer is coordinating more than one
            event, so each event keeps its own chat, checklist and vendor list. */}
        {eventDates.length > 1 && (
          <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="coordinator-event-picker">
            <span className="text-xs font-medium text-muted-foreground">Coordinating:</span>
            {eventDates.map((d) => (
              <button key={d} onClick={() => setEventDate(d)}
                className={`text-xs font-semibold rounded-full px-3 py-1.5 btn-anim cursor-pointer border ${eventDate === d ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground hover:text-foreground hover:bg-secondary/60'}`}
                data-testid={`event-tab-${d}`}>
                {fmtEventDate(d)}
              </button>
            ))}
          </div>
        )}

        <div className="grid lg:grid-cols-[340px_1fr] gap-6 items-start">
          {/* Left: event summary + checklist — pinned so it stays in view and the page doesn't run long */}
          <aside className="space-y-4 lg:sticky lg:top-24 self-start">
            {/* Countdown + vendors */}
            <div className="bg-card border border-border/70 rounded-2xl shadow-soft p-5" data-testid="coordinator-event-summary">
              {loading ? (
                <div className="h-24 bg-secondary/60 rounded-xl animate-pulse" />
              ) : upcoming.length === 0 ? (
                <div className="text-center py-4">
                  <CalendarCheck size={28} className="text-primary mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-3">No upcoming events yet. Book your vendors and your coordinator kicks in.</p>
                  <Link to="/ai-planner" className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-xl px-4 h-10 text-xs font-semibold btn-anim shadow-soft hover:bg-primary/90">Plan an event <ArrowRight size={13} /></Link>
                </div>
              ) : (
                <>
                  {countdown !== null && (
                    <div className="text-center pb-4 mb-4 border-b border-border/60">
                      <div className="text-4xl font-display font-semibold text-primary tabular-nums leading-none">{countdown <= 0 ? 'Today' : countdown}</div>
                      <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">{countdown <= 0 ? "It's event day!" : countdown === 1 ? 'day to go' : 'days to go'}</div>
                    </div>
                  )}
                  <p className="text-xs font-semibold text-foreground mb-2">Your booked vendors{upcoming.length > 3 ? ` (${upcoming.length})` : ''}</p>
                  <div className="space-y-2 max-h-[248px] overflow-y-auto pr-1 -mr-1">
                    {upcoming.map(b => (
                      <div key={b.booking_id} className="bg-secondary/40 rounded-xl px-3 py-2.5" data-testid={`coordinator-vendor-${b.booking_id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-foreground truncate">{b.provider?.business_name || b.category || 'Vendor'}</span>
                          <span className={`text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded-full shrink-0 ${b.status === 'confirmed' ? 'bg-emerald-500/10 text-emerald-600' : b.status === 'completed' ? 'bg-primary/10 text-primary' : 'bg-amber-500/10 text-amber-600'}`}>{b.status}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[11px] text-muted-foreground">
                          <span className="flex items-center gap-1"><CalendarCheck size={11} /> {b.booking_date}</span>
                          {b.start_time && <span className="flex items-center gap-1"><Clock size={11} /> {b.start_time}</span>}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1"><CurrencyDollar size={11} /> {payLabel(b)}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Coordination checklist — checkable + persisted */}
            <div className="bg-card border border-border/70 rounded-2xl shadow-soft p-5">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-foreground flex items-center gap-1.5"><ListChecks size={15} className="text-primary" /> Coordination checklist</p>
                <span className="text-[11px] font-medium text-muted-foreground tabular-nums" data-testid="checklist-progress">{doneKeys.length}/{CHECKLIST.length}</span>
              </div>
              {/* Progress bar */}
              <div className="h-1.5 bg-secondary rounded-full mb-3 overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${(doneKeys.length / CHECKLIST.length) * 100}%` }} />
              </div>
              <div className="space-y-0.5">
                {CHECKLIST.map((c) => {
                  const isDone = doneKeys.includes(c.key);
                  const urgent = c.key === 'payments' && balanceDue;
                  return (
                    <div key={c.key} className="flex items-start gap-2 rounded-xl px-1.5 py-1.5 hover:bg-secondary/40 transition-colors group"
                      data-testid={`coordinator-task-${c.key}`}>
                      {/* Checkbox toggles done state (persisted) */}
                      <button onClick={() => toggleTask(c.key)} aria-label={isDone ? `Mark "${c.title}" not done` : `Mark "${c.title}" done`}
                        className={`shrink-0 mt-0.5 w-4 h-4 rounded-[5px] border flex items-center justify-center cursor-pointer transition-colors ${isDone ? 'bg-primary border-primary text-primary-foreground' : 'border-border hover:border-primary bg-card'}`}
                        data-testid={`checklist-toggle-${c.key}`}>
                        {isDone && <CheckCircle size={12} weight="bold" />}
                      </button>
                      {/* Title/desc — clicking asks the coordinator about this task */}
                      <button onClick={() => send(c.prompt)} disabled={sending}
                        className="min-w-0 text-left flex-1 cursor-pointer disabled:opacity-50">
                        <span className={`block text-[13px] font-medium transition-colors ${isDone ? 'text-muted-foreground line-through' : 'text-foreground group-hover:text-primary'}`}>
                          {c.title}
                          {urgent && <span className="ml-1.5 align-middle inline-block text-[9px] font-bold uppercase tracking-wide text-amber-700 bg-amber-500/15 rounded px-1 py-0.5 no-underline">Due soon</span>}
                        </span>
                        {!isDone && <span className="block text-[11px] text-muted-foreground leading-snug">{c.desc}</span>}
                      </button>
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-muted-foreground mt-2.5">Tap a task to ask your coordinator about it, or check it off when done.</p>
            </div>
          </aside>

          {/* Right: coordinator chat */}
          <section className="bg-card border border-border/70 rounded-2xl shadow-soft flex flex-col h-[72vh] min-h-[520px] overflow-hidden" data-testid="coordinator-chat">
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-5 py-5 space-y-4">
              {messages.map((m, i) => (
                m.role === 'assistant' ? (
                  (() => {
                    const { text, sends } = parseVendorMsgs(m.content);
                    return (
                      <div key={i} className="flex items-start gap-2.5">
                        <span className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5"><Sparkle size={13} weight="fill" /></span>
                        <div className="max-w-[88%] space-y-2">
                          {text && <div className="bg-secondary/60 rounded-2xl rounded-tl-md px-4 py-3 text-sm text-foreground/90 leading-relaxed break-words" dangerouslySetInnerHTML={{ __html: mdToHtml(text) }} />}
                          {sends.map((s, si) => {
                            const st = sentSends[sendKey(s)];
                            return (
                              <div key={si} className="bg-card border border-primary/25 rounded-2xl rounded-tl-md p-3 shadow-soft" data-testid="coordinator-vendor-send">
                                <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-primary mb-1.5">
                                  <ChatCircle size={12} weight="fill" /> Message to {s.name}
                                </div>
                                <p className="text-sm text-foreground/85 leading-relaxed mb-2.5 whitespace-pre-wrap">{s.body}</p>
                                <button
                                  onClick={() => sendVendorMsg(s)}
                                  disabled={st === 'sending' || st === 'sent'}
                                  className={`inline-flex items-center gap-1.5 h-9 px-4 text-xs font-semibold rounded-xl btn-anim shadow-soft cursor-pointer disabled:cursor-default ${st === 'sent' ? 'bg-emerald-500/15 text-emerald-700 border border-emerald-300' : st === 'blocked' ? 'bg-amber-500/15 text-amber-700 border border-amber-300' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}
                                  data-testid="coordinator-send-btn">
                                  {st === 'sent'
                                    ? <><CheckCircle size={14} weight="fill" /> Sent to {s.name}</>
                                    : st === 'sending'
                                      ? <>Sending…</>
                                      : st === 'blocked'
                                        ? <><PaperPlaneRight size={13} weight="fill" /> Held — rephrase & retry</>
                                        : st === 'error'
                                          ? <><PaperPlaneRight size={13} weight="fill" /> Retry send to {s.name}</>
                                          : <><PaperPlaneRight size={13} weight="fill" /> Send to {s.name}</>}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div key={i} className="flex justify-end">
                    <div className="bg-primary text-primary-foreground rounded-2xl rounded-tr-md px-4 py-3 text-sm leading-relaxed max-w-[88%] whitespace-pre-wrap">{m.content}</div>
                  </div>
                )
              ))}
              {(sending || briefing) && (
                <div className="flex items-start gap-2.5" data-testid="coordinator-typing">
                  <span className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5"><Sparkle size={13} weight="fill" /></span>
                  <div className="bg-secondary/60 rounded-2xl rounded-tl-md px-4 py-3.5 flex items-center gap-1">
                    {[0, 150, 300].map(d => <span key={d} className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
                  </div>
                </div>
              )}
              {!started && !sending && !briefing && (
                <div className="flex flex-wrap gap-2 pt-1 pl-9" data-testid="coordinator-suggestions">
                  {suggestions.map(s => (
                    <button key={s} onClick={() => send(s)} className="text-[12px] font-medium text-primary border border-primary/30 bg-primary/5 rounded-full px-3 py-1.5 btn-anim hover:bg-primary/10 cursor-pointer">{s}</button>
                  ))}
                </div>
              )}
            </div>
            <div className="border-t border-border/70 p-3">
              <div className="flex items-end gap-2">
                <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={onKeyDown} rows={1} placeholder="Ask your coordinator anything…"
                  className="flex-1 resize-none max-h-32 rounded-xl border border-border bg-background px-3.5 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" data-testid="coordinator-input" />
                <button onClick={() => send()} disabled={sending || !input.trim()} aria-label="Send"
                  className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center btn-anim shadow-soft hover:bg-primary/90 disabled:opacity-40 cursor-pointer shrink-0" data-testid="coordinator-send">
                  <PaperPlaneRight size={17} weight="fill" />
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground text-center mt-2">Your coordinator can draft messages, timelines & checklists — it can’t send messages or move money for you.</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
